/**
 * 判断两个对象是否完全内容相等（非引用相等）
 * 复杂对象，将会递归判断
 * @author luoying
 * @since 17/06/03
 */
import { getTag, isArray, isFunction, isNull } from './check-types';
const stringTag = '[object String]';
const numberTag = '[object Number]';
const boolTag = '[object Boolean]';
const dateTag = '[object Date]';
const regExpTag = '[object RegExp]';
const symbolTag = '[object Symbol]';
const arrayTag = '[object Array]';
const objectTag = '[object Object]';
const mapTag = '[object Map]';
const setTag = '[object Set]';
function isObjEqual(obj1, obj2) {
    const keys1 = Object.keys(obj1);
    let len1 = keys1.length;
    if (len1 !== Object.keys(obj2).length) {
        return false;
    }
    let eq = true;
    while (len1--) {
        const key = keys1[len1];
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (!isEqual(obj1[key], obj2[key])) {
            eq = false;
            break;
        }
    }
    const c1 = obj1.constructor;
    const c2 = obj2.constructor;
    if (eq && c1 != c2 && 'constructor' in obj1 && 'constructor' in obj2) {
        eq = false;
    }
    return eq;
}
function isArrEqual(arr1, arr2) {
    if (arr1.length !== arr2.length)
        return false;
    let len = arr1.length;
    while (len--) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (!isEqual(arr1[len], arr2[len]))
            return false;
    }
    return true;
}
function isMapEqual(map1, map2) {
    if (map1.size !== map2.size)
        return false;
    for (const key of map1.keys()) {
        const v1 = map1.get(key);
        const v2 = map2.get(key);
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (!isEqual(v1, v2))
            return false;
    }
    return true;
}
function isSetEqual(set1, set2) {
    if (set1.size !== set2.size)
        return false;
    const arr = new Array(set2.size);
    set2.forEach(v => (arr[arr.length] = v));
    for (const v1 of set1.values()) {
        let found = false;
        for (let i = 0; i < arr.length; i++) {
            const v2 = arr[i];
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            if (isEqual(v1, v2)) {
                found = true;
                arr.splice(i, 1);
                break;
            }
        }
        if (!found)
            return false;
    }
    return true;
}
function equalByTag(obj1, obj2, tag) {
    switch (tag) {
        case boolTag:
        case numberTag:
        case dateTag: {
            const v1 = +obj1;
            const v2 = +obj2;
            return v1 === v2 || (v1 !== v1 && v2 !== v2);
        }
        case stringTag:
        case regExpTag: {
            return obj1 == `${obj2}`;
        }
        case symbolTag: {
            const valueOf = Symbol.prototype.valueOf;
            return valueOf.call(obj1) === valueOf.call(obj2);
        }
        case mapTag: {
            return isMapEqual(obj1, obj2);
        }
        case setTag: {
            return isSetEqual(obj1, obj2);
        }
    }
    return false;
}
/**
 * 检测两个值是否完全相等（非引用相等）
 * @export
 * @param {*} [obj1] 值1
 * @param {*} [obj2] 值2
 * @returns {boolean}
 */
export function isEqual(obj1, obj2) {
    // strict equal
    if (obj1 === obj2)
        return true;
    // null to null
    if (isNull(obj1) || isNull(obj2)) {
        return obj1 === obj2;
    }
    const tag1 = getTag(obj1);
    const tag2 = getTag(obj2);
    // tag equal
    if (tag1 !== tag2)
        return false;
    if (tag1 !== arrayTag && tag1 !== objectTag) {
        // equals by tag
        return equalByTag(obj1, obj2, tag1);
    }
    // equals by Array or Object
    return isArray(obj1) ? isArrEqual(obj1, obj2) : isObjEqual(obj1, obj2);
}
export default isEqual;
export function isReactPropsEqual(nextProps, prevProps, excludeKeys = []) {
    const propKeys = Object.keys(nextProps);
    const notCheckKeys = excludeKeys.concat(['children', 'ref']);
    for (const key of propKeys) {
        if (notCheckKeys.includes(key))
            continue;
        const val = nextProps[key];
        if (isFunction(val))
            continue;
        if (!isEqual(val, prevProps[key])) {
            return false;
        }
    }
    return true;
}
export function isReactStateEqual(nextState, prevState) {
    const stateKeys = Object.keys(nextState);
    for (const key of stateKeys) {
        const val = nextState[key];
        if (!isEqual(val, prevState[key])) {
            return false;
        }
    }
    return true;
}
//# sourceMappingURL=equal.js.map